import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

export default class extends BridgeComponent {
  static component = "push-token"

  connect() {
    super.connect()
    this.#invalidateCacheIfSessionChanged()
    this.send("connect", {}, (message) => {
      this.#handleCallback(message)
    })
  }

  // Wipe the stored token cache whenever the auth session changes
  // (sign-in, sign-out + sign-in as a different user). Without this,
  // a stale token cached from a previous session would short-circuit
  // the POST and leave the new user with no device row server-side.
  #invalidateCacheIfSessionChanged() {
    const currentSessionId = this.element.dataset.sessionId
    if (!currentSessionId) return
    const sessionKey = "floosyo.lastRegisteredPushSessionId"
    const previousSessionId = localStorage.getItem(sessionKey)
    if (previousSessionId !== currentSessionId) {
      localStorage.removeItem("floosyo.lastRegisteredPushToken")
      localStorage.setItem(sessionKey, currentSessionId)
    }
  }

  /// Prompts iOS for push permission. Resolves with { granted, status }.
  /// Use from other controllers (e.g. onboarding) via bridge dispatch.
  requestPermission() {
    return new Promise((resolve) => {
      this.send("requestPermission", {}, (message) => {
        if (message?.event === "permissionResult") {
          resolve(message.data || { granted: false, status: "unknown" })
        }
      })
    })
  }

  #handleCallback(message) {
    const { event, data } = message

    if (event === "register" && data?.token) {
      this.#registerToken(data.token, data.platform, data.name)
    }
  }

  async #registerToken(token, platform, name) {
    // No registration endpoint means this element was rendered pre-auth
    // (the onboarding notification step). The native permission prompt still
    // works; the token is registered later once the authenticated layout
    // element mounts with a URL. Don't cache here, so that later mount still
    // POSTs the token.
    const url = this.element.dataset.pushSubscriptionUrl
    if (!url) return

    // Don't even attempt when the user disabled notifications in-app. Disabling
    // doesn't revoke the OS permission, so the native side keeps emitting
    // `register` on every page connect (settings → password → back, etc). The
    // element carries the server preference; honour it and skip — otherwise we
    // POST a registration the server only 403s, on every navigation.
    if (this.element.dataset.pushTokenDisabled === "true") return

    // Within-webview dedupe + retry suppression. The cache stops re-POSTing the
    // same token on every Turbo navigation. (The simultaneous cross-webview
    // burst is collapsed natively in PushTokenComponent — localStorage is NOT
    // synchronously shared across separate WKWebView processes, so it can't be
    // relied on for that.)
    const cacheKey = "floosyo.lastRegisteredPushToken"
    if (localStorage.getItem(cacheKey) === token) return
    localStorage.setItem(cacheKey, token)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          push_subscription: { token, platform, name }
        })
      })
      // 403 = disabled server-side: keep the claim so we STOP re-POSTing on
      // every navigation. Re-enabling clears the cache explicitly, which
      // re-allows registration. Other failures are transient — drop the claim
      // so a genuine retry can register later.
      if (!response.ok && response.status !== 403) localStorage.removeItem(cacheKey)
    } catch (error) {
      localStorage.removeItem(cacheKey)
      console.error("[push-token] Registration failed:", error)
    }
  }
}
